import { useCallback, useMemo, useRef } from 'react';

import useAssetsStore, { selectAllAssets } from 'hooks/stores/useAssetsStore';
import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';

export default function useAutomateUserWorkflow(selectedProductId?: string) {
  const { products, selectAssetForSide } = useProductsStore((state) => ({
    products: state.products,
    selectAssetForSide: state.selectAssetForSide,
  }));

  const { sidesCount, quantity } = useSessionStore((state) => {
    return {
      sidesCount: state.session?.sidesCount,
      quantity: state.session?.quantity,
    };
  });

  // Gebruik de helper selector om alle assets op te halen
  const allAssets = useAssetsStore(selectAllAssets);
  // Ook de assetIds opvragen, voor lengte check
  const assetIds = useAssetsStore((state) => state.allIds);

  const hasAutomatedWorkFlowRef = useRef(false);

  const automateUserWorkflowInSingleSide = useCallback(() => {
    // Find the target product (selected or first)
    const targetProduct = selectedProductId
      ? products.find((p) => p.id === selectedProductId)
      : products[0];

    const side = targetProduct?.sides[0];
    // Gebruik de eerste asset uit de lijst
    const firstAsset = allAssets[0];

    if (side?.id && firstAsset && targetProduct) {
      selectAssetForSide({
        assetId: firstAsset.id,
        sideId: side.id,
        productId: targetProduct.id,
      });
      hasAutomatedWorkFlowRef.current = true;
    }
  }, [allAssets, products, selectAssetForSide, selectedProductId]);

  const shouldSkipAutomateWorkflow: boolean = useMemo(() => {
    if (hasAutomatedWorkFlowRef.current) return true;
    const isSingleSideInSingleProduct = quantity === 1 && (sidesCount ?? 0) === 1;
    if (!isSingleSideInSingleProduct) return true;
    const targetProduct = selectedProductId
      ? products.find((p) => p.id === selectedProductId)
      : products[0];
    const isThereAssetIdInFirstSide = targetProduct?.sides[0]?.assetId;
    if (!isThereAssetIdInFirstSide) return true;
    // Check aantal assets via assetIds.length
    if (assetIds.length === 0) return true;
    return false;
  }, [assetIds.length, products, quantity, sidesCount, selectedProductId]);

  return {
    shouldSkipAutomateWorkflow,
    automateUserWorkflowInSingleSide,
  };
}
